import { serve } from '@hono/node-server'
import as from 'ansis'
import type { Context } from 'hono'

import { createMockServer } from '@scalar/mock-server'
import type { OpenAPI, OpenAPIV3_1 } from '@scalar/openapi-types'

import { output } from '@/helpers/std-out'

export const bootServer = async ({
  specification,
  port,
}: {
  specification: OpenAPI.Document
  port?: number
}): Promise<ReturnType<typeof serve>> => {
  const app = await createMockServer({
    specification,
    onRequest,
  })

  return serve(
    {
      fetch: app.fetch,
      port: port ?? 3000,
    },
    (info) => {
      output
        .info()
        .message(
          `${as.green.bold('➜ Mock Server')} ${as.white(
            'listening on',
          )} ${as.cyan(`http://localhost:${info.port}`)}`,
        )
        .print()
    },
  )
}

export const printAvailablePaths = (specification: OpenAPI.Document) => {
  output.info().message(as.white.bold('Available Paths')).print()

  if (
    specification?.paths === undefined ||
    Object.keys(specification?.paths).length === 0
  ) {
    output
      .warn()
      .title(as.grey('Could not find any paths in the OpenAPI file.'))
      .print()
  }

  // loop through all paths
  for (const path in specification?.paths ?? []) {
    if (specification?.paths?.[path] === undefined) {
      continue
    }

    // loop through all methods
    for (const method in specification.paths[path]) {
      // @ts-expect-error - we know that the path exists
      if (specification.paths[path][method] === undefined) {
        continue
      }

      output
        .info()
        .line(
          `${as[getMethodColor(method)].bold(method.toUpperCase().padEnd(6))} ${as.grey(`${path}`)}`,
        )
        .print()
    }
  }

  output.info().line().print()
}

const onRequest = ({
  context,
  operation,
}: {
  context: Context<any, any, {}>
  operation: OpenAPIV3_1.OperationObject<{}>
}) => {
  const { method } = context.req

  output
    .info()
    .line(
      `${as[getMethodColor(method)].bold(method.toUpperCase().padEnd(6))} ${as.grey(`${context.req.path}`)} ${as.grey('→')} ${
        operation?.operationId
          ? as.white(operation.operationId)
          : as.red('[ERROR] 404 Not Found')
      }`,
    )
    .print()
}

export type OutColor =
  | 'black'
  | 'red'
  | 'green'
  | 'yellow'
  | 'blue'
  | 'magenta'
  | 'cyan'
  | 'white'
  | 'gray'
  | 'grey'

export function getMethodColor(method: string) {
  const colors: Record<string, OutColor> = {
    get: 'green',
    post: 'cyan',
    put: 'yellow',
    delete: 'red',
    patch: 'magenta',
  }

  return (colors[method.toLowerCase()] ?? 'grey') as OutColor
}
